type CognitoTokenKey = "idToken" | "accessToken" | "refreshToken";

const TOKEN_STORAGE_KEYS: Record<CognitoTokenKey, string> = {
  idToken: "cognito.idToken",
  accessToken: "cognito.accessToken",
  refreshToken: "cognito.refreshToken",
};

const USERNAME_STORAGE_KEY = "cognito.username";
const CLIENT_ID_ENV = "VITE_COGNITO_USER_POOL_CLIENT_ID";
const USER_POOL_ID_ENV = "VITE_COGNITO_USER_POOL_ID";

export class CognitoException extends Error {
  code: string;
  statusCode?: number;
  constructor(message: string, code: string, statusCode?: number) {
    super(message);
    this.name = code;
    this.code = code;
    this.statusCode = statusCode;
  }

  get isUserNotFound(): boolean {
    return (
      this.code === "UserNotFoundException" ||
      this.name === "UserNotFoundException"
    );
  }
}

class AuthToken {
  constructor(private readonly value: string) {}
  toString(): string {
    return this.value;
  }
}

export interface AuthSession {
  tokens?: {
    idToken?: AuthToken;
    accessToken?: AuthToken;
  };
}

let activeChallengeSession: string | null = null;

function requiredEnv(key: string): string {
  const value = import.meta.env[key];
  if (!value || typeof value !== "string") {
    throw new Error(`Missing required env var: ${key}`);
  }
  return value;
}

function getClientId(): string {
  return requiredEnv(CLIENT_ID_ENV);
}

function getRegion(): string {
  const poolId = requiredEnv(USER_POOL_ID_ENV);
  const region = poolId.split("_")[0];
  if (!region) {
    throw new Error(`Invalid ${USER_POOL_ID_ENV} format`);
  }
  return region;
}

function getEndpoint(): string {
  return `https://cognito-idp.${getRegion()}.amazonaws.com/`;
}

function readStorage(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStorage(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Ignore storage failures.
  }
}

function removeStorage(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    // Ignore storage failures.
  }
}

function readToken(key: CognitoTokenKey): string | null {
  return readStorage(TOKEN_STORAGE_KEYS[key]);
}

function persistTokens(tokens: {
  idToken: string;
  accessToken: string;
  refreshToken: string;
}): void {
  writeStorage(TOKEN_STORAGE_KEYS.idToken, tokens.idToken);
  writeStorage(TOKEN_STORAGE_KEYS.accessToken, tokens.accessToken);
  writeStorage(TOKEN_STORAGE_KEYS.refreshToken, tokens.refreshToken);
}

function persistRefreshedTokens(tokens: {
  idToken: string;
  accessToken: string;
}): void {
  writeStorage(TOKEN_STORAGE_KEYS.idToken, tokens.idToken);
  writeStorage(TOKEN_STORAGE_KEYS.accessToken, tokens.accessToken);
}

function parseJwtPayload(
  token: string | null,
): Record<string, unknown> | undefined {
  if (!token) return undefined;
  const parts = token.split(".");
  if (parts.length < 2) return undefined;
  try {
    const payload = JSON.parse(atob(parts[1])) as Record<string, unknown>;
    return payload && typeof payload === "object" ? payload : undefined;
  } catch {
    return undefined;
  }
}

function parseJwtSubject(token: string | null): string | undefined {
  const payload = parseJwtPayload(token);
  return typeof payload?.sub === "string" ? payload.sub : undefined;
}

function parseJwtExpiryMs(token: string | null): number | undefined {
  const payload = parseJwtPayload(token);
  const exp = payload?.exp;
  return typeof exp === "number" && Number.isFinite(exp) ? exp * 1000 : undefined;
}

/** Access token still usable (with skew), so a refresh is unnecessary. */
function isAccessTokenFresh(skewMs = 60_000): boolean {
  const expiryMs = parseJwtExpiryMs(readToken("accessToken"));
  if (!expiryMs) return false;
  return expiryMs > Date.now() + skewMs;
}

/** Cognito errors that mean the refresh token is gone for good. */
function isDefinitiveAuthFailure(error: unknown): boolean {
  if (!(error instanceof CognitoException)) return false;
  switch (error.code) {
    case "NotAuthorizedException":
    case "UserNotFoundException":
    case "InvalidParameterException":
    case "ResourceNotFoundException":
      return true;
    default:
      return false;
  }
}

async function cognitoCall<TResponse extends object>(
  operation: string,
  body: Record<string, unknown>,
): Promise<TResponse> {
  const response = await fetch(getEndpoint(), {
    method: "POST",
    headers: {
      "Content-Type": "application/x-amz-json-1.1",
      "X-Amz-Target": `AWSCognitoIdentityProviderService.${operation}`,
    },
    body: JSON.stringify(body),
  });

  const json = (await response.json().catch(() => ({}))) as {
    __type?: string;
    message?: string;
    Message?: string;
  } & Record<string, unknown>;

  if (!response.ok) {
    const rawType = typeof json.__type === "string" ? json.__type : "";
    const code = rawType.split("#").pop() || rawType || "CognitoError";
    const message =
      (typeof json.message === "string" && json.message) ||
      (typeof json.Message === "string" && json.Message) ||
      `${operation} failed`;
    throw new CognitoException(message, code, response.status);
  }

  return json as TResponse;
}

export function normalizeEmail(email: string): string {
  return email.trim().toLowerCase();
}

export function clearAuthTokens(): void {
  removeStorage(TOKEN_STORAGE_KEYS.idToken);
  removeStorage(TOKEN_STORAGE_KEYS.accessToken);
  removeStorage(TOKEN_STORAGE_KEYS.refreshToken);
  removeStorage(USERNAME_STORAGE_KEY);
}

export function getStoredUsername(): string | null {
  return readStorage(USERNAME_STORAGE_KEY);
}

export function getAccessToken(): string | null {
  return readToken("accessToken");
}

export async function fetchAuthSession(): Promise<AuthSession> {
  const accessToken = readToken("accessToken");
  const idToken = readToken("idToken");
  if (!accessToken) {
    return {};
  }
  return {
    tokens: {
      accessToken: new AuthToken(accessToken),
      idToken: idToken ? new AuthToken(idToken) : undefined,
    },
  };
}

function generateTemporaryPassword(): string {
  const upper = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  const lower = "abcdefghijklmnopqrstuvwxyz";
  const digits = "0123456789";
  const symbols = "!@#$%^&*";
  const all = upper + lower + digits + symbols;
  const pick = (chars: string) =>
    chars[Math.floor(Math.random() * chars.length)];

  let password = [pick(upper), pick(lower), pick(digits), pick(symbols)].join("");
  while (password.length < 20) {
    password += pick(all);
  }
  return password;
}

export async function signUp(email: string, password?: string): Promise<void> {
  const username = normalizeEmail(email);
  await cognitoCall("SignUp", {
    ClientId: getClientId(),
    Username: username,
    Password: password ?? generateTemporaryPassword(),
  });
}

export async function initiateCustomAuth(email: string): Promise<void> {
  const username = normalizeEmail(email);
  const result = await cognitoCall<{ Session?: string }>("InitiateAuth", {
    AuthFlow: "CUSTOM_AUTH",
    ClientId: getClientId(),
    AuthParameters: {
      USERNAME: username,
    },
  });
  activeChallengeSession = result.Session ?? null;
  writeStorage(USERNAME_STORAGE_KEY, username);
}

export async function respondToCustomChallenge(code: string): Promise<void> {
  const username = getStoredUsername();
  if (!username) {
    throw new CognitoException("Missing login user", "InvalidStateException");
  }
  if (!activeChallengeSession) {
    throw new CognitoException("Missing challenge session", "InvalidStateException");
  }

  const result = await cognitoCall<{
    AuthenticationResult?: {
      IdToken?: string;
      AccessToken?: string;
      RefreshToken?: string;
    };
  }>("RespondToAuthChallenge", {
    ChallengeName: "CUSTOM_CHALLENGE",
    ClientId: getClientId(),
    Session: activeChallengeSession,
    ChallengeResponses: {
      USERNAME: username,
      ANSWER: code,
    },
  });

  const auth = result.AuthenticationResult;
  if (!auth?.IdToken || !auth.AccessToken || !auth.RefreshToken) {
    throw new CognitoException("Authentication incomplete", "NotAuthorizedException");
  }

  persistTokens({
    idToken: auth.IdToken,
    accessToken: auth.AccessToken,
    refreshToken: auth.RefreshToken,
  });
  activeChallengeSession = null;
}

export async function refreshTokens(options?: {
  force?: boolean;
}): Promise<boolean> {
  // Avoid a Cognito round-trip (and its failure modes) on every boot/reload
  // while the current access token is still valid — common during Vite
  // full reloads from .env / vite.config edits in development.
  if (!options?.force && isAccessTokenFresh()) {
    return true;
  }

  const refreshToken = readToken("refreshToken");
  if (!refreshToken) return false;
  try {
    const result = await cognitoCall<{
      AuthenticationResult?: {
        IdToken?: string;
        AccessToken?: string;
      };
    }>("InitiateAuth", {
      AuthFlow: "REFRESH_TOKEN_AUTH",
      ClientId: getClientId(),
      AuthParameters: {
        REFRESH_TOKEN: refreshToken,
      },
    });
    const auth = result.AuthenticationResult;
    if (!auth?.IdToken || !auth.AccessToken) return false;
    persistRefreshedTokens({
      idToken: auth.IdToken,
      accessToken: auth.AccessToken,
    });
    return true;
  } catch (error) {
    // Only wipe the session when Cognito says the refresh token is invalid.
    // Network blips / 5xx during restore used to clear tokens and look like
    // an accidental logout on app update.
    if (isDefinitiveAuthFailure(error)) {
      clearAuthTokens();
    }
    return false;
  }
}

export async function signOut(): Promise<void> {
  const refreshToken = readToken("refreshToken");

  // Revoke only this device's refresh token. GlobalSignOut invalidates every
  // refresh token for the user across all devices/sessions, which meant a
  // routine web logout was silently kicking the user out of the mobile app
  // too — RevokeToken is the per-device equivalent Cognito provides.
  if (refreshToken) {
    await cognitoCall("RevokeToken", {
      ClientId: getClientId(),
      Token: refreshToken,
    }).catch(() => undefined);
  }

  clearAuthTokens();
  activeChallengeSession = null;
}

export async function getCurrentUser(): Promise<{
  username: string;
  userId?: string;
  signInDetails?: { loginId?: string };
}> {
  const accessToken = readToken("accessToken");
  const idToken = readToken("idToken");
  const username = getStoredUsername();
  if (!accessToken || !username) {
    throw new CognitoException("Not signed in", "NotAuthorizedException");
  }
  return {
    username,
    userId: parseJwtSubject(idToken),
    signInDetails: { loginId: username },
  };
}

export async function fetchUserAttributes(): Promise<Record<string, string>> {
  const accessToken = readToken("accessToken");
  if (!accessToken) {
    throw new CognitoException("Not signed in", "NotAuthorizedException");
  }
  const result = await cognitoCall<{
    UserAttributes?: Array<{ Name?: string; Value?: string }>;
    Username?: string;
  }>("GetUser", {
    AccessToken: accessToken,
  });

  const attributes: Record<string, string> = {};
  for (const item of result.UserAttributes ?? []) {
    if (!item?.Name || typeof item.Value !== "string") continue;
    attributes[item.Name] = item.Value;
  }

  if (!getStoredUsername()) {
    const inferred = normalizeEmail(attributes.email || result.Username || "");
    if (inferred) {
      writeStorage(USERNAME_STORAGE_KEY, inferred);
    }
  }

  return attributes;
}

export async function initiateUserAuth(email: string): Promise<void> {
  const username = normalizeEmail(email);
  const result = await cognitoCall<{ Session?: string }>("InitiateAuth", {
    AuthFlow: "USER_AUTH",
    ClientId: getClientId(),
    AuthParameters: {
      USERNAME: username,
    },
  });
  activeChallengeSession = result.Session ?? null;
}
